import React, { useRef, useState } from 'react';
import { Flex, Input, IconButton } from '@chakra-ui/react';
import { ArrowUpIcon, AttachmentIcon } from '@chakra-ui/icons';
import { useIntl } from 'react-intl';

// Client-side gate mirroring the server whitelist (images + common docs). The
// server re-validates size + MIME, so this is only a UX hint.
const ACCEPT =
  'image/png,image/jpeg,image/gif,image/webp,application/pdf,text/plain,application/zip';

interface MessageInputProps {
  onSend: (text: string) => void;
  onSendFile?: (file: File, caption?: string) => void;
  disabled?: boolean;
}

const MessageInput: React.FC<MessageInputProps> = ({
  onSend,
  onSendFile,
  disabled,
}) => {
  const intl = useIntl();
  const [value, setValue] = useState('');
  const fileRef = useRef<HTMLInputElement | null>(null);

  const submit = () => {
    const text = value.trim();
    if (!text) return;
    onSend(text);
    setValue('');
  };

  const onPickFile = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file && onSendFile) {
      onSendFile(file, value.trim() || undefined);
      setValue('');
    }
    // Reset so picking the same file again re-fires change.
    if (fileRef.current) fileRef.current.value = '';
  };

  const onKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      submit();
    }
  };

  return (
    <Flex p={2} gap={2} borderTopWidth="1px" borderColor="border.subtle">
      {onSendFile && (
        <>
          <input
            ref={fileRef}
            type="file"
            accept={ACCEPT}
            style={{ display: 'none' }}
            onChange={onPickFile}
          />
          <IconButton
            size="sm"
            variant="ghost"
            aria-label={intl.formatMessage({ id: 'support.attach' })}
            icon={<AttachmentIcon />}
            onClick={() => fileRef.current?.click()}
            isDisabled={disabled}
          />
        </>
      )}
      <Input
        size="sm"
        variant="filled"
        value={value}
        onChange={(e) => setValue(e.target.value)}
        onKeyDown={onKeyDown}
        placeholder={intl.formatMessage({ id: 'support.inputPlaceholder' })}
        // Chakra's default placeholder is ~2.1:1 on the filled field in dark.
        _placeholder={{ opacity: 1, color: 'text.secondary' }}
        aria-label={intl.formatMessage({ id: 'support.inputPlaceholder' })}
      />
      <IconButton
        size="sm"
        colorScheme="green"
        aria-label={intl.formatMessage({ id: 'support.send' })}
        icon={<ArrowUpIcon />}
        onClick={submit}
        isDisabled={disabled || !value.trim()}
      />
    </Flex>
  );
};

export default MessageInput;
